import {
  Alert,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import type { SxProps, Theme } from "@mui/material/styles";
import { memo, useCallback, useMemo, useState, type ChangeEvent } from "react";
import {
  CompanyAccountType,
  type CompanyAccountTypesQuery,
  useCompanyAccountTypesQuery,
  useSetCompanyAccountTypeMutation,
} from "../../generated/graphql";
import { throwOnGraphqlErrors } from "../../pages/api-keys-admin/throw-on-graphql-errors";

type CompanyRow = CompanyAccountTypesQuery["companies"][number];

const ACCOUNT_TYPES = Object.values(CompanyAccountType);

const styles = {
  panel: { padding: 4 } satisfies SxProps<Theme>,
  table: { maxWidth: 800 } satisfies SxProps<Theme>,
  select: { minWidth: 160 } satisfies SxProps<Theme>,
};

type AccountTypeRowProps = {
  readonly company: CompanyRow;
  readonly saving: boolean;
  readonly onChange: (uuid: string, accountType: CompanyAccountType) => void;
};

const AccountTypeRow = memo(
  ({ company, saving, onChange }: AccountTypeRowProps) => {
    const handleChange = useCallback(
      (event: SelectChangeEvent<CompanyAccountType>) => {
        onChange(company.uuid, event.target.value as CompanyAccountType);
      },
      [company.uuid, onChange],
    );
    return (
      <TableRow>
        <TableCell>{company.name}</TableCell>
        <TableCell>
          <Select
            size="small"
            sx={styles.select}
            value={company.accountType}
            disabled={saving}
            inputProps={{ "aria-label": `Account type for ${company.name}` }}
            onChange={handleChange}
          >
            {ACCOUNT_TYPES.map((accountType) => (
              <MenuItem key={accountType} value={accountType}>
                {accountType}
              </MenuItem>
            ))}
          </Select>
        </TableCell>
      </TableRow>
    );
  },
);
AccountTypeRow.displayName = "AccountTypeRow";

/**
 * Tenant classification. Tenet broadcasts reach CUSTOMER and INTERNAL
 * companies only; mark test and demo tenants so they are left out.
 */
const CompanyAccountTypePanel = () => {
  const { data, error: loadError, refetch } = useCompanyAccountTypesQuery();
  const [setAccountType, { loading: saving }] =
    useSetCompanyAccountTypeMutation();
  const [filter, setFilter] = useState("");
  const [saveError, setSaveError] = useState<string | null>(null);

  const companies = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    return [...(data?.companies ?? [])]
      .filter((company) => company.name.toLowerCase().includes(needle))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data, filter]);

  const handleFilter = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setFilter(event.target.value);
  }, []);

  const handleChange = useCallback(
    (companyUuid: string, accountType: CompanyAccountType) => {
      setSaveError(null);
      setAccountType({ variables: { input: { companyUuid, accountType } } })
        .then(async (result) => {
          throwOnGraphqlErrors(result);
          await refetch();
        })
        .catch((error: unknown) => {
          setSaveError(error instanceof Error ? error.message : String(error));
        });
    },
    [setAccountType, refetch],
  );

  return (
    <Stack spacing={2} sx={styles.panel}>
      <Typography variant="h6">Account types</Typography>
      <Typography variant="body2" color="text.secondary">
        Tenet broadcasts reach CUSTOMER and INTERNAL companies only.
      </Typography>
      {loadError !== undefined && (
        <Alert severity="error">{loadError.message}</Alert>
      )}
      {saveError !== null && <Alert severity="error">{saveError}</Alert>}
      <TextField
        size="small"
        label="Filter companies"
        value={filter}
        onChange={handleFilter}
      />
      <Table size="small" sx={styles.table}>
        <TableHead>
          <TableRow>
            <TableCell>Company</TableCell>
            <TableCell>Account type</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {companies.map((company) => (
            <AccountTypeRow
              key={company.uuid}
              company={company}
              saving={saving}
              onChange={handleChange}
            />
          ))}
        </TableBody>
      </Table>
    </Stack>
  );
};

export default CompanyAccountTypePanel;
